if (!customElements.get('setup-product-card')) {
  customElements.define(
    'setup-product-card',
    class SetupProductCard extends HTMLElement {
      connectedCallback() {
        this.isAdding = false;
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.variantInput = this.querySelector('[data-setup-product-variant-id]');
        this.quantityInput = this.querySelector('[data-setup-product-quantity]');
        this.image = this.querySelector('.setup-products-card__image');
        this.error = this.querySelector('[data-setup-product-error]');
        this.addButton = this.querySelector('[data-setup-product-add]');
        this.addButtonLabel = this.querySelector('[data-setup-product-add-label]');
        this.loadingSpinner = this.addButton?.querySelector('.loading__spinner');
        this.optionButtons = Array.from(this.querySelectorAll('[data-setup-product-option]'));
        this.variants = this.getVariants();

        const currentVariant = this.getCurrentVariant() || this.variants[0];
        this.currentOptionValues = currentVariant ? [...currentVariant.options] : [];

        this.bindEvents();

        if (currentVariant) {
          this.updateFromVariant(currentVariant);
        }
      }

      getVariants() {
        const variantsScript = this.querySelector('[data-setup-product-variants]');
        if (!variantsScript) return [];

        try {
          return JSON.parse(variantsScript.textContent);
        } catch (error) {
          console.error(error);
          return [];
        }
      }

      bindEvents() {
        this.optionButtons.forEach((button) => {
          button.addEventListener('click', this.onOptionClick.bind(this));
        });

        this.querySelector('[data-setup-product-decrease]')?.addEventListener('click', () => {
          this.updateQuantity((this.getQuantity() || 1) - 1);
        });

        this.querySelector('[data-setup-product-increase]')?.addEventListener('click', () => {
          this.updateQuantity((this.getQuantity() || 1) + 1);
        });

        this.quantityInput?.addEventListener('input', () => {
          this.updateQuantity(this.getQuantity());
        });

        this.addButton?.addEventListener('click', this.onAddToCart.bind(this));
      }

      getCurrentVariant() {
        if (!this.variantInput) return null;

        return this.variants.find((variant) => `${variant.id}` === `${this.variantInput.value}`);
      }

      getQuantity() {
        const quantity = Number(this.quantityInput?.value || 1);
        return Number.isFinite(quantity) && quantity > 0 ? Math.floor(quantity) : 1;
      }

      updateQuantity(quantity) {
        if (!this.quantityInput) return;
        this.quantityInput.value = Math.max(1, quantity);
        this.updateAddButtonLabel(this.getCurrentVariant());
      }

      onOptionClick(event) {
        const optionIndex = Number(event.currentTarget.dataset.optionIndex);
        const optionValue = event.currentTarget.dataset.optionValue;
        const nextOptionValues = [...this.currentOptionValues];
        nextOptionValues[optionIndex] = optionValue;

        const matchingVariant = this.findMatchingVariant(nextOptionValues, true) || this.findMatchingVariant(nextOptionValues);
        if (!matchingVariant) return;

        this.hideError();
        this.updateFromVariant(matchingVariant);
      }

      findMatchingVariant(optionValues, requireAvailable = false) {
        return (
          this.variants.find((variant) => {
            if (requireAvailable && !variant.available) return false;

            return variant.options.every((value, index) => optionValues[index] === value);
          }) || null
        );
      }

      hasAvailableVariantForSelection(optionIndex, optionValue) {
        const nextOptionValues = [...this.currentOptionValues];
        nextOptionValues[optionIndex] = optionValue;

        return Boolean(this.findMatchingVariant(nextOptionValues, true));
      }

      hasExistingVariantForSelection(optionIndex, optionValue) {
        const nextOptionValues = [...this.currentOptionValues];
        nextOptionValues[optionIndex] = optionValue;

        return Boolean(this.findMatchingVariant(nextOptionValues));
      }

      updateFromVariant(variant) {
        this.variantInput.value = variant.id;
        this.currentOptionValues = [...variant.options];

        if (this.image && variant.imageUrl) {
          this.image.removeAttribute('srcset');
          this.image.removeAttribute('sizes');
          this.image.src = variant.imageUrl;
          this.image.alt = variant.imageAlt || this.image.alt;
        }

        if (this.addButton && this.addButtonLabel) {
          const isAvailable = Boolean(variant.available);
          this.addButton.disabled = !isAvailable;
          this.addButton.setAttribute('aria-disabled', isAvailable ? 'false' : 'true');
          this.updateAddButtonLabel(variant);
        }

        this.updateOptionState();
      }

      updateAddButtonLabel(variant) {
        if (!this.addButtonLabel || !variant) return;

        if (!variant.available) {
          this.addButtonLabel.textContent = this.dataset.soldOutLabel;
          return;
        }

        const label = variant.isPreorder ? this.dataset.preorderLabel : this.dataset.addLabel;
        const totalPrice = Number(variant.price) * this.getQuantity();
        const priceLabel = Number.isFinite(totalPrice) ? this.formatMoney(totalPrice) : variant.priceLabel;
        this.addButtonLabel.innerHTML = `${label} ${priceLabel}`;
      }

      formatMoney(cents) {
        const format = this.dataset.moneyFormat || '${{amount}}';
        const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
        const placeholder = format.match(placeholderRegex)?.[1] || 'amount';

        const formatWithDelimiters = (value, precision = 2, thousands = ',', decimal = '.') => {
          const number = Number(value || 0) / 100;
          const parts = number.toFixed(precision).split('.');
          parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
          return parts.join(decimal);
        };

        const formattedAmount = {
          amount: formatWithDelimiters(cents, 2),
          amount_no_decimals: formatWithDelimiters(cents, 0),
          amount_with_comma_separator: formatWithDelimiters(cents, 2, '.', ','),
          amount_no_decimals_with_comma_separator: formatWithDelimiters(cents, 0, '.', ','),
          amount_with_apostrophe_separator: formatWithDelimiters(cents, 2, "'", '.'),
        }[placeholder] || formatWithDelimiters(cents, 2);

        return format.replace(placeholderRegex, formattedAmount);
      }

      updateOptionState() {
        this.optionButtons.forEach((button) => {
          const optionIndex = Number(button.dataset.optionIndex);
          const optionValue = button.dataset.optionValue;
          const isSelected = this.currentOptionValues[optionIndex] === optionValue;
          const hasExistingVariant = this.hasExistingVariantForSelection(optionIndex, optionValue);
          const hasAvailableVariant = this.hasAvailableVariantForSelection(optionIndex, optionValue);

          button.classList.toggle('is-selected', isSelected);
          button.classList.toggle('is-unavailable', !hasAvailableVariant);
          button.setAttribute('aria-pressed', isSelected ? 'true' : 'false');
          button.disabled = !isSelected && (!hasExistingVariant || !hasAvailableVariant);
        });
      }

      setLoading(isLoading) {
        if (!this.addButton || !this.loadingSpinner) return;

        this.addButton.classList.toggle('loading', isLoading);
        this.addButton.setAttribute('aria-disabled', isLoading ? 'true' : this.addButton.disabled ? 'true' : 'false');
        this.loadingSpinner.classList.toggle('hidden', !isLoading);
      }

      showError(message) {
        if (!this.error) return;

        this.error.textContent = message;
        this.error.hidden = !message;
      }

      hideError() {
        this.showError('');
      }

      onAddToCart() {
        if (!this.variantInput || !this.addButton || this.addButton.disabled || this.isAdding) return;

        this.isAdding = true;
        this.hideError();
        this.setLoading(true);

        const config = typeof fetchConfig === 'function' ? fetchConfig('javascript') : { method: 'POST', headers: { Accept: 'application/json' } };
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData();
        formData.append('id', this.variantInput.value);
        formData.append('quantity', this.getQuantity());

        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          if (typeof this.cart.setActiveElement === 'function') {
            this.cart.setActiveElement(document.activeElement);
          }
        }

        config.body = formData;

        fetch((window.routes && routes.cart_add_url) || '/cart/add', config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
                publish(PUB_SUB_EVENTS.cartError, {
                  source: 'setup-product-card',
                  productVariantId: formData.get('id'),
                  errors: response.errors || response.description,
                  message: response.message,
                });
              }

              this.showError(response.description || response.message);
              return;
            }

            if (!this.cart) {
              window.location = (window.routes && routes.cart_url) || '/cart';
              return;
            }

            if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'setup-product-card',
                productVariantId: formData.get('id'),
                cartData: response,
              });
            }

            this.cart.renderContents(response);
          })
          .catch((error) => {
            console.error(error);
          })
          .finally(() => {
            if (this.cart && this.cart.classList.contains('is-empty')) {
              this.cart.classList.remove('is-empty');
            }

            this.isAdding = false;
            this.setLoading(false);
          });
      }
    }
  );
}
